"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { Suspense, useState, useEffect, useRef, useMemo, useCallback, useDeferredValue } from "react";
import { useSearchParams } from "next/navigation";
import type { Card } from "@/lib/api/types";
import { cardHasKeyword } from "@/lib/card-display";
import { cachedFetch } from "@/lib/fetch-cache";
import CardGrid from "@/app/components/CardGrid";
import FullCardGrid from "@/app/components/FullCardGrid";
import SearchFilter from "@/app/components/SearchFilter";
import { CDN_BASE } from "@/lib/image-url";
import { useChannel } from "@/lib/api/prefix.client";
import { useEntityScores } from "@/lib/use-entity-scores";
import { useBetaAdditions } from "@/lib/use-beta-additions";
import { replaceQuery } from "@/lib/replace-query";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const sortOptions = [{
  label: "Top tier",
  value: "score"
}, {
  label: "A → Z",
  value: "az"
}, {
  label: "Z → A",
  value: "za"
}, {
  label: "Cost: low → high",
  value: "cost"
}, {
  label: "Cost: high → low",
  value: "cost-desc"
}, {
  label: "Compendium",
  value: "compendium"
}];
const colorOptions = [{
  label: "Ironclad",
  value: "ironclad"
}, {
  label: "Silent",
  value: "silent"
}, {
  label: "Defect",
  value: "defect"
}, {
  label: "Necrobinder",
  value: "necrobinder"
}, {
  label: "Regent",
  value: "regent"
}, {
  label: "Colorless",
  value: "colorless"
}];
const typeOptions = [{
  label: "Attack",
  value: "Attack"
}, {
  label: "Skill",
  value: "Skill"
}, {
  label: "Power",
  value: "Power"
}, {
  label: "Quest",
  value: "Quest"
}, {
  label: "Status",
  value: "Status"
}, {
  label: "Curse",
  value: "Curse"
}];
const rarityOptions = [{
  label: "Basic",
  value: "Basic"
}, {
  label: "Common",
  value: "Common"
}, {
  label: "Uncommon",
  value: "Uncommon"
}, {
  label: "Rare",
  value: "Rare"
}, {
  label: "Ancient",
  value: "Ancient"
}, {
  label: "Event",
  value: "Event"
}, {
  label: "Status",
  value: "Status"
}, {
  label: "Curse",
  value: "Curse"
}, {
  label: "Token",
  value: "Token"
}, {
  label: "Quest",
  value: "Quest"
}];

// The game's blank energy orb marks energy costs and the star icon marks
// Ancient star costs (served from the CDN), so the two groups can't be
// confused; the group names are translated at render time.
const ENERGY_ICON = `${CDN_BASE}/icons/colorless_energy_icon.webp`;
const STAR_ICON = `${CDN_BASE}/icons/star_icon.webp`;
const costOptions = [{
  label: "0",
  value: "0",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "1",
  value: "1",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "2",
  value: "2",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "3",
  value: "3",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "4+",
  value: "4plus",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "X",
  value: "x",
  group: "Energy",
  icon: ENERGY_ICON
}, {
  label: "Any",
  value: "starany",
  group: "Star",
  icon: STAR_ICON
}, {
  label: "1",
  value: "star1",
  group: "Star",
  icon: STAR_ICON
}, {
  label: "2",
  value: "star2",
  group: "Star",
  icon: STAR_ICON
}, {
  label: "3",
  value: "star3",
  group: "Star",
  icon: STAR_ICON
}, {
  label: "4+",
  value: "star4plus",
  group: "Star",
  icon: STAR_ICON
}, {
  label: "X",
  value: "starx",
  group: "Star",
  icon: STAR_ICON
}];
function matchesCost(c: Card, want: string): boolean {
  if (!want) return true;
  if (want === "x") return !!c.is_x_cost;
  if (want === "starx") return !!c.is_x_star_cost;
  if (want === "starany") return typeof c.star_cost === "number" || !!c.is_x_star_cost;
  if (want.startsWith("star")) {
    if (typeof c.star_cost !== "number") return false;
    return want === "star4plus" ? c.star_cost >= 4 : want === `star${c.star_cost}`;
  }
  if (typeof c.cost !== "number" || c.cost < 0) return false;
  return want === "4plus" ? c.cost >= 4 : want === String(c.cost);
}
function energyRank(c: Card): number {
  if (c.is_x_cost) return 100;
  if (typeof c.cost !== "number" || c.cost < 0) return Infinity;
  return c.cost;
}
function starRank(c: Card): number {
  if (c.is_x_star_cost) return 100;
  return typeof c.star_cost === "number" ? c.star_cost : -1;
}
function compareCost(a: Card, b: Card, dir: 1 | -1): number {
  const ea = energyRank(a);
  const eb = energyRank(b);
  if (ea !== eb) {
    if (ea === Infinity) return 1;
    if (eb === Infinity) return -1;
    return (ea - eb) * dir;
  }
  const sa = starRank(a);
  const sb = starRank(b);
  if (sa !== sb) return (sa - sb) * dir;
  return a.name.localeCompare(b.name);
}
const keywordOptions = [{
  label: "Exhaust",
  value: "Exhaust"
}, {
  label: "Innate",
  value: "Innate"
}, {
  label: "Ethereal",
  value: "Ethereal"
}, {
  label: "Retain",
  value: "Retain"
}, {
  label: "Unplayable",
  value: "Unplayable"
}, {
  label: "Sly",
  value: "Sly"
}, {
  label: "Eternal",
  value: "Eternal"
}];
function CardsClientInner(t0) {
  const $ = _c(141);
  const {
    initialCards,
    initialBeta
  } = t0;
  const searchParams = useSearchParams();
  const [cards, setCards] = useState(initialCards);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("search") || "";
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [search, setSearch] = useState(t1);
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.get("color") || "";
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [color, setColor] = useState(t2);
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams.get("type") || "";
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [type, setType] = useState(t3);
  let t4;
  if ($[6] !== searchParams) {
    t4 = searchParams.get("rarity") || "";
    $[6] = searchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [rarity, setRarity] = useState(t4);
  let t5;
  if ($[8] !== searchParams) {
    t5 = searchParams.get("keyword") || "";
    $[8] = searchParams;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const [keyword, setKeyword] = useState(t5);
  let t6;
  if ($[10] !== searchParams) {
    t6 = searchParams.get("cost") || "";
    $[10] = searchParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [cost, setCost] = useState(t6);
  let t7;
  if ($[12] !== searchParams) {
    t7 = searchParams.get("sort") || "az";
    $[12] = searchParams;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const [sort, setSort] = useState(t7);
  const [view, setView] = useState("card");
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const saved = localStorage.getItem("cards-view-v2");
      if (saved === "card" || saved === "detail") {
        setView(saved);
      }
    };
    t9 = [];
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = v => {
      setView(v);
      localStorage.setItem("cards-view-v2", v);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const pickView = t10;
  const lang = useGameLocale();
  const t = useT();
  const channel = useChannel();
  const betaAdditions = useBetaAdditions("cards", lang, initialBeta);
  const initialRender = useRef(true);
  const updateUrl = _temp;
  let t11;
  if ($[17] !== color || $[18] !== cost || $[19] !== keyword || $[20] !== rarity || $[21] !== search || $[22] !== sort || $[23] !== type) {
    t11 = (key, value, setter) => {
      setter(value);
      const current = {
        search,
        color,
        type,
        rarity,
        keyword,
        cost,
        sort
      };
      current[key] = value;
      updateUrl(current);
    };
    $[17] = color;
    $[18] = cost;
    $[19] = keyword;
    $[20] = rarity;
    $[21] = search;
    $[22] = sort;
    $[23] = type;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const setFilterAndUrl = t11;
  let t12;
  let t13;
  if ($[25] !== searchParams) {
    t12 = () => {
      setColor(searchParams.get("color") || "");
      setType(searchParams.get("type") || "");
      setRarity(searchParams.get("rarity") || "");
      setKeyword(searchParams.get("keyword") || "");
      setCost(searchParams.get("cost") || "");
      setSort(searchParams.get("sort") || "az");
    };
    t13 = [searchParams];
    $[25] = searchParams;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  useEffect(t12, t13);
  let t14;
  if ($[28] !== channel || $[29] !== color || $[30] !== cost || $[31] !== initialCards || $[32] !== keyword || $[33] !== lang || $[34] !== rarity || $[35] !== search || $[36] !== type || $[37] !== view) {
    t14 = () => {
      if (initialRender.current) {
        initialRender.current = false;
        if (channel !== "beta" && lang === "eng" && !color && !type && !rarity && !keyword && !cost && !search && view === "card" && initialCards.length > 0) {
          return;
        }
      }
      const params_0 = new URLSearchParams();
      if (color) {
        params_0.set("color", color);
      }
      if (type) {
        params_0.set("type", type);
      }
      if (rarity) {
        params_0.set("rarity", rarity);
      }
      if (keyword) {
        params_0.set("keyword", keyword);
      }
      if (cost) {
        params_0.set("cost", cost);
      }
      if (search) {
        params_0.set("search", search);
      }
      params_0.set("lang", lang);
      cachedFetch(`${API}/api/cards?${params_0}`).then(setCards);
    };
    $[28] = channel;
    $[29] = color;
    $[30] = cost;
    $[31] = initialCards;
    $[32] = keyword;
    $[33] = lang;
    $[34] = rarity;
    $[35] = search;
    $[36] = type;
    $[37] = view;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== channel || $[40] !== color || $[41] !== cost || $[42] !== keyword || $[43] !== lang || $[44] !== rarity || $[45] !== search || $[46] !== type || $[47] !== view) {
    t15 = [color, type, rarity, keyword, cost, search, lang, channel, view];
    $[39] = channel;
    $[40] = color;
    $[41] = cost;
    $[42] = keyword;
    $[43] = lang;
    $[44] = rarity;
    $[45] = search;
    $[46] = type;
    $[47] = view;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  useEffect(t14, t15);
  const scores = useEntityScores("cards");
  let t16;
  bb0: {
    if (betaAdditions.length === 0) {
      t16 = cards;
      break bb0;
    }
    let t17;
    if ($[49] !== betaAdditions) {
      t17 = new Set(betaAdditions.map(_temp2));
      $[49] = betaAdditions;
      $[50] = t17;
    } else {
      t17 = $[50];
    }
    const ids = t17;
    let t18;
    if ($[51] !== betaAdditions || $[52] !== cards || $[53] !== color || $[54] !== cost || $[55] !== ids || $[56] !== keyword || $[57] !== rarity || $[58] !== search || $[59] !== type) {
      let t19;
      if ($[61] !== color || $[62] !== cost || $[63] !== keyword || $[64] !== rarity || $[65] !== search || $[66] !== type) {
        t19 = c_0 => (!color || c_0.color === color) && (!type || c_0.type === type) && (!rarity || c_0.rarity === rarity) && (!keyword || cardHasKeyword(c_0, keyword)) && matchesCost(c_0, cost) && (!search || c_0.name.toLowerCase().includes(search.toLowerCase()));
        $[61] = color;
        $[62] = cost;
        $[63] = keyword;
        $[64] = rarity;
        $[65] = search;
        $[66] = type;
        $[67] = t19;
      } else {
        t19 = $[67];
      }
      const additions = betaAdditions.filter(t19).map(_temp3);
      let t20;
      if ($[68] !== ids) {
        t20 = c_2 => !ids.has(c_2.id);
        $[68] = ids;
        $[69] = t20;
      } else {
        t20 = $[69];
      }
      t18 = [...cards.filter(t20), ...additions];
      $[51] = betaAdditions;
      $[52] = cards;
      $[53] = color;
      $[54] = cost;
      $[55] = ids;
      $[56] = keyword;
      $[57] = rarity;
      $[58] = search;
      $[59] = type;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    t16 = t18;
  }
  const withBeta = t16;
  let sorted;
  if ($[70] !== scores || $[71] !== sort || $[72] !== withBeta) {
    sorted = [...withBeta];
    if (sort === "az") {
      sorted.sort(_temp4);
    } else {
      if (sort === "za") {
        sorted.sort(_temp5);
      } else {
        if (sort === "cost") {
          sorted.sort(_temp6);
        } else {
          if (sort === "cost-desc") {
            sorted.sort(_temp7);
          } else {
            if (sort === "compendium") {
              sorted.sort(_temp8);
            } else {
              if (sort === "score") {
                let t17;
                if ($[74] !== scores) {
                  t17 = (a_4, b_4) => {
                    const sa = scores[a_4.id.toUpperCase()]?.score ?? -1;
                    const sb = scores[b_4.id.toUpperCase()]?.score ?? -1;
                    if (sb !== sa) {
                      return sb - sa;
                    }
                    return a_4.compendium_order - b_4.compendium_order;
                  };
                  $[74] = scores;
                  $[75] = t17;
                } else {
                  t17 = $[75];
                }
                sorted.sort(t17);
              }
            }
          }
        }
      }
    }
    $[70] = scores;
    $[71] = sort;
    $[72] = withBeta;
    $[73] = sorted;
  } else {
    sorted = $[73];
  }
  const sortedCards = sorted;
  const gridCards = useDeferredValue(sortedCards);
  let t17;
  if ($[76] !== setFilterAndUrl) {
    t17 = v_1 => setFilterAndUrl("search", v_1, setSearch);
    $[76] = setFilterAndUrl;
    $[77] = t17;
  } else {
    t17 = $[77];
  }
  let t18;
  if ($[78] !== t) {
    t18 = t("Search cards...");
    $[78] = t;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  const t19 = sortedCards.length;
  let t20;
  if ($[80] !== t) {
    t20 = sortOptions.map(o => ({
      ...o,
      label: t(o.label)
    }));
    $[80] = t;
    $[81] = t20;
  } else {
    t20 = $[81];
  }
  let t21;
  if ($[82] !== setFilterAndUrl || $[83] !== view) {
    t21 = v_2 => {
      setFilterAndUrl("sort", v_2, setSort);
      if (v_2 === "score" && view !== "card") {
        pickView("card");
      }
    };
    $[82] = setFilterAndUrl;
    $[83] = view;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  let t23;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = [{
      value: "card",
      label: "Card View"
    }, {
      value: "detail",
      label: "Detail View"
    }];
    t23 = v_3 => pickView(v_3 as "card" | "detail");
    $[85] = t22;
    $[86] = t23;
  } else {
    t22 = $[85];
    t23 = $[86];
  }
  let t24;
  if ($[87] !== view) {
    t24 = {
      label: "View",
      name: "View",
      value: view,
      options: t22,
      onChange: t23,
      noEmptyOption: true
    };
    $[87] = view;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== setFilterAndUrl) {
    t25 = v_4 => setFilterAndUrl("color", v_4, setColor);
    $[89] = setFilterAndUrl;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== color || $[92] !== t25) {
    t26 = {
      label: "Any Color",
      name: "Color",
      value: color,
      options: colorOptions,
      onChange: t25
    };
    $[91] = color;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== setFilterAndUrl) {
    t27 = v_5 => setFilterAndUrl("type", v_5, setType);
    $[94] = setFilterAndUrl;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] !== t27 || $[97] !== type) {
    t28 = {
      label: "Any Type",
      name: "Type",
      value: type,
      options: typeOptions,
      onChange: t27
    };
    $[96] = t27;
    $[97] = type;
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  let t29;
  if ($[99] !== setFilterAndUrl) {
    t29 = v_6 => setFilterAndUrl("rarity", v_6, setRarity);
    $[99] = setFilterAndUrl;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  let t30;
  if ($[101] !== rarity || $[102] !== t29) {
    t30 = {
      label: "Any Rarity",
      name: "Rarity",
      value: rarity,
      options: rarityOptions,
      onChange: t29
    };
    $[101] = rarity;
    $[102] = t29;
    $[103] = t30;
  } else {
    t30 = $[103];
  }
  let t31;
  if ($[104] !== t) {
    t31 = costOptions.map(o_0 => ({
      ...o_0,
      label: o_0.value === "starany" ? t(o_0.label) : o_0.label,
      group: o_0.group === "Energy" ? t("Energy") : t("Star Cost")
    }));
    $[104] = t;
    $[105] = t31;
  } else {
    t31 = $[105];
  }
  let t32;
  if ($[106] !== setFilterAndUrl) {
    t32 = v_7 => setFilterAndUrl("cost", v_7, setCost);
    $[106] = setFilterAndUrl;
    $[107] = t32;
  } else {
    t32 = $[107];
  }
  let t33;
  if ($[108] !== cost || $[109] !== t31 || $[110] !== t32) {
    t33 = {
      label: "Any Cost",
      name: "Cost",
      value: cost,
      options: t31,
      onChange: t32
    };
    $[108] = cost;
    $[109] = t31;
    $[110] = t32;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  let t34;
  if ($[112] !== setFilterAndUrl) {
    t34 = v_8 => setFilterAndUrl("keyword", v_8, setKeyword);
    $[112] = setFilterAndUrl;
    $[113] = t34;
  } else {
    t34 = $[113];
  }
  let t35;
  if ($[114] !== keyword || $[115] !== t34) {
    t35 = {
      label: "Any Keyword",
      name: "Keyword",
      value: keyword,
      options: keywordOptions,
      onChange: t34
    };
    $[114] = keyword;
    $[115] = t34;
    $[116] = t35;
  } else {
    t35 = $[116];
  }
  let t36;
  if ($[117] !== t24 || $[118] !== t26 || $[119] !== t28 || $[120] !== t30 || $[121] !== t33 || $[122] !== t35) {
    t36 = [t24, t26, t28, t30, t33, t35];
    $[117] = t24;
    $[118] = t26;
    $[119] = t28;
    $[120] = t30;
    $[121] = t33;
    $[122] = t35;
    $[123] = t36;
  } else {
    t36 = $[123];
  }
  let t37;
  if ($[124] !== search || $[125] !== sort || $[126] !== sortedCards.length || $[127] !== t17 || $[128] !== t18 || $[129] !== t20 || $[130] !== t21 || $[131] !== t36) {
    t37 = <SearchFilter search={search} onSearchChange={t17} placeholder={t18} resultCount={t19} sortOptions={t20} sortValue={sort} onSortChange={t21} filters={t36} />;
    $[124] = search;
    $[125] = sort;
    $[126] = sortedCards.length;
    $[127] = t17;
    $[128] = t18;
    $[129] = t20;
    $[130] = t21;
    $[131] = t36;
    $[132] = t37;
  } else {
    t37 = $[132];
  }
  let t38;
  if ($[133] !== gridCards || $[134] !== scores || $[135] !== sort || $[136] !== view) {
    t38 = view === "card" ? <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)]/40 p-3 sm:p-5"><FullCardGrid cards={gridCards} stats={sort === "score" ? scores : undefined} /></div> : <CardGrid cards={gridCards} />;
    $[133] = gridCards;
    $[134] = scores;
    $[135] = sort;
    $[136] = view;
    $[137] = t38;
  } else {
    t38 = $[137];
  }
  let t39;
  if ($[138] !== t37 || $[139] !== t38) {
    t39 = <>{t37}{t38}</>;
    $[138] = t37;
    $[139] = t38;
    $[140] = t39;
  } else {
    t39 = $[140];
  }
  return t39;
}

// useSearchParams needs a Suspense boundary above it now that the root
// layout no longer provides one (the app-wide boundary made every dynamic
// page's body invisible to non-JS crawlers). The boundary lives here so
// every page that renders this client, English and localized, gets it.
function _temp8(a_3, b_3) {
  return a_3.compendium_order - b_3.compendium_order;
}
function _temp7(a_2, b_2) {
  return compareCost(a_2, b_2, -1);
}
function _temp6(a_1, b_1) {
  return compareCost(a_1, b_1, 1);
}
function _temp5(a_0, b_0) {
  return b_0.name.localeCompare(a_0.name);
}
function _temp4(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp3(c_1) {
  return {
    ...c_1,
    beta: true
  };
}
function _temp2(c) {
  return c.id;
}
function _temp(newState) {
  const params = new URLSearchParams();
  for (const [k, v_0] of Object.entries(newState)) {
    if (v_0 && v_0 !== "az") {
      params.set(k, v_0);
    }
  }
  const qs = params.toString();
  replaceQuery(qs);
}
export default function CardsClient(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="min-h-screen" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><CardsClientInner {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}